import medicationImageUrl from "@/assets/medication.svg"
import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import HeroImage from "@/components/HeroImage"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import {
  useGetMedicationRemindersQuery,
  useSelfQuery,
  useSetMedicationRemindersMutation,
  useUpdatePatientProfileMutation,
} from "@/data/queries"
import type { PatientAppState } from "@/model"
import { ROUTES } from "@/routes"
import MedicationReminders from "@/screens/onboarding/components/MedicationReminders"
import { useState } from "react"
import { useNavigate } from "react-router"

type MedicationRemindersFormProps = {
  patientId: string
  initialReminderTimes: string[]
  existingState: PatientAppState | undefined
}

// Split out so `initialReminderTimes` seeds useState on mount only — later
// refetches must not clobber in-progress edits.
const MedicationRemindersForm = ({
  patientId,
  initialReminderTimes,
  existingState,
}: MedicationRemindersFormProps) => {
  const navigate = useNavigate()
  const { mutateAsync: setReminders, isPending: isSavingReminders } =
    useSetMedicationRemindersMutation()
  const { mutateAsync: updateProfile, isPending: isUpdatingProfile } =
    useUpdatePatientProfileMutation()

  const [reminderTimes, setReminderTimes] = useState<string[]>(initialReminderTimes)

  const isPending = isSavingReminders || isUpdatingProfile

  const handleSubmit = async () => {
    await setReminders({ patientId, reminderTimes })
    await updateProfile({
      patientId,
      profile: {
        patientAppState: {
          profileFormFilled: true,
          bloodGlucoseDeviceSetupCompleted:
            existingState?.bloodGlucoseDeviceSetupCompleted ?? false,
        },
      },
    })

    if (existingState?.bloodGlucoseDeviceSetupCompleted) {
      navigate(ROUTES.DASHBOARD, { state: { initialLogin: true } })
    } else {
      navigate(ROUTES.BLOOD_GLUCOSE_DEVICE_SETUP)
    }
  }

  return (
    <>
      <MedicationReminders reminderTimes={reminderTimes} onChange={setReminderTimes} />

      <Button
        type="button"
        disabled={isPending}
        className="mt-auto flex w-full gap-3 py-2 text-base"
        onClick={handleSubmit}
      >
        {isPending && <Spinner />}
        Save and continue
      </Button>
    </>
  )
}

const MedicationRemindersStep = () => {
  const { data: self } = useSelfQuery()
  const patientId = self?.id ?? ""

  const { data: existingReminders, isPending: isLoadingReminders } = useGetMedicationRemindersQuery(
    { patientId },
  )

  return (
    <Page showAppbar={false}>
      <div className="absolute left-4 top-4 z-10">
        <BackButton />
      </div>
      <HeroImage src={medicationImageUrl} alt="Medication" />
      <div className="flex min-h-[calc(100vh-40vh-56px)] flex-col gap-6">
        <p className="text-base text-primary-contrast">
          What time(s) each day would you want to be reminded to take your medication?
        </p>

        {isLoadingReminders || existingReminders === undefined ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : (
          <MedicationRemindersForm
            patientId={patientId}
            initialReminderTimes={existingReminders}
            existingState={self?.patientAppState}
          />
        )}
      </div>
    </Page>
  )
}

export default MedicationRemindersStep
